import { Children, type ReactNode } from "react";

import classes from "./MasonryGrid.module.css";

export interface MasonryGridProps {
  children: ReactNode;
  /** The column count: the page's persisted setting (`libraryColumns`, `productColumns`). */
  columns: number;
  /** Names the gallery for a screen reader, as a group. */
  label?: string;
  /** Pixels between columns and cards: 4 as the library has it, 0 where cards pad themselves. */
  gap?: number;
}

/**
 * The live app's galleries (design-system.md 3.8): equal columns 4px apart, the items dealt to
 * them in turn (item n goes to column n mod count), so each column stacks cards of their own
 * heights. Each column rises into place from below, 100ms after the one before.
 */
export function MasonryGrid({ children, columns, label, gap = 4 }: MasonryGridProps) {
  const count = Math.max(1, Math.floor(columns));
  const lanes: ReactNode[][] = Array.from({ length: count }, () => []);
  Children.toArray(children).forEach((child, index) => lanes[index % count]?.push(child));
  return (
    <div
      className={classes.grid}
      style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gap }}
      role={label === undefined ? undefined : "group"}
      aria-label={label}
    >
      {lanes.map((lane, index) => (
        <div
          key={index}
          className={classes.column}
          style={{ animationDelay: `${index * 100}ms`, gap }}
        >
          {lane}
        </div>
      ))}
    </div>
  );
}
